import { zodResolver } from '@hookform/resolvers/zod'
import { Divider, Grid, Stack } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { Form } from 'components/Form'
import FormInput from 'components/FormInput'
import InputDatePicker from 'components/FormInputDatePicker'
import InputMask from 'components/FormInputMask'
import { FormInputSelect } from 'components/FormInputSelect'
import Label from 'components/Label'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  familiarReferenceSchema,
  FamiliarReferenceType,
  personReferenceInit,
} from 'types/familiar-reference.type'
import { phoneMask } from 'utils/mask.util'
import { AddressGroup } from '../AddressGroup'
import { useFamiliarReferenceOptions } from './hooks/select-option.hook'

export type ActionType = 'save' | 'visualize'

type DialogFormProps = {
  type: ActionType
  title: string
  open: boolean
  data: FamiliarReferenceType
  onSave: (data: FamiliarReferenceType) => Promise<void>
  onEdit: (data: FamiliarReferenceType) => void
  onDelete: (data: FamiliarReferenceType) => void
  onCancel: () => void
  loading: boolean
}

export const DialogForm: React.FC<DialogFormProps> = ({
  type,
  title,
  open,
  data,
  onSave,
  onEdit,
  onDelete,
  onCancel,
  loading,
}) => {
  const familiarReferenceOptions = useFamiliarReferenceOptions()

  const methods = useForm<FamiliarReferenceType>({
    mode: 'all',
    resolver: zodResolver(familiarReferenceSchema),
    defaultValues: personReferenceInit,
  })

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    getValues,
    formState: { isDirty },
  } = methods

  const phoneNumberValue = watch(`telefone`)

  const handleSave = (familiarReference: FamiliarReferenceType): void => {
    if (isDirty) {
      onSave(familiarReference).then(() => reset())
    }
  }

  const handleEdit = (): void => {
    const familiarReference = getValues()
    onEdit(familiarReference)
  }

  const handleDelete = (): void => {
    const familiarReference = getValues()
    onDelete(familiarReference)
  }

  const handleCancel = () => {
    reset()
  }

  useEffect(() => {
    reset(data)
  }, [data])

  useEffect(() => setValue(`telefone`, phoneMask(phoneNumberValue)), [setValue, phoneNumberValue])

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth='lg'>
      <Form methods={methods} onSubmit={handleSubmit(handleSave)}>
        <DialogTitle>
          <Label text={title} sx={{ color: 'primary.main' }} bold />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} rowSpacing={4} sx={{ mt: '1px' }}>
            <Grid item md={4} xs={12}>
              <FormInputSelect
                control={control}
                name='tipoReferencia.id'
                label='Tipo de Referência*'
                disabled={type === 'visualize'}
                options={familiarReferenceOptions}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <FormInput
                control={control}
                name='pessoaReferencia.nome'
                label='Nome*'
                maxLength={25}
                disabled={type === 'visualize'}
                placeholder='Informe o Nome'
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <InputDatePicker
                control={control}
                name='pessoaReferencia.dataNascimento'
                label='Data de nascimento'
                disabled={type === 'visualize'}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <InputMask
                control={control}
                name='pessoaReferencia.cpf'
                label='CPF'
                mask='000.000.000-00'
                disabled={type === 'visualize'}
                placeholder='Informe o Número do CPF'
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <FormInput
                control={control}
                name='pessoaReferencia.identidade'
                label='Identidade'
                disabled={type === 'visualize'}
                placeholder='Informe o Número da Identidade'
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <FormInput
                control={control}
                name='telefone'
                label='Telefone'
                maxLength={15}
                disabled={type === 'visualize'}
                placeholder='Informe o Número de Telefone'
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <FormInput
                control={control}
                name='email'
                label='Email'
                maxLength={60}
                disabled={type === 'visualize'}
                placeholder='Informe o Email'
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormInput
                control={control}
                name='descricaoProfissao'
                label='Profissão/Ocupação'
                maxLength={60}
                disabled={type === 'visualize'}
                placeholder='Informe a Profissão/Ocupação'
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormInput
                control={control}
                name='descricaoRendaBeneficio'
                label='Renda/Benefício'
                maxLength={60}
                disabled={type === 'visualize'}
                placeholder='Informe a Renda/Benefício'
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                control={control}
                name='informacaoComplementar'
                label='Informações Complementares'
                placeholder='Informe as Informações Complementares'
                maxLength={210}
                multiline
                disabled={type === 'visualize'}
                minRows={3}
              />
            </Grid>
            <Grid item xs>
              <Divider variant='fullWidth' />
            </Grid>
            <Grid container item>
              <Stack direction='column' spacing={2}>
                <Label text='Endereço' bold />
                <AddressGroup type={type} control={control} watch={watch} setValue={setValue} />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Stack direction='row' spacing={2}>
            {type === 'save' && (
              <Button
                variant='contained'
                color='secondary'
                onClick={handleSubmit((data) => handleSave(data))}
                sx={{ width: 120 }}
                disabled={loading}
              >
                Salvar
              </Button>
            )}
            {type === 'visualize' && (
              <>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={handleEdit}
                  sx={{ width: 120 }}
                >
                  Editar
                </Button>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={handleDelete}
                  sx={{ width: 120 }}
                >
                  Excluir
                </Button>
              </>
            )}
            <Button variant='contained' color='secondary' onClick={onCancel} sx={{ width: 120 }}>
              Cancelar
            </Button>
          </Stack>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
